import { provideHttpClient, withInterceptors, withJsonpSupport } from '@angular/common/http';
import { catchError, firstValueFrom, timeout } from 'rxjs';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  TranslateService,
  provideMissingTranslationHandler,
  provideTranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import Aura from '@primeuix/themes/aura';
import { MessageService, ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';
import { RegistrationTrackingService } from './core/services/registration-tracking.service';
import { AuthBootstrapService } from './core/services/auth-bootstrap.service';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ChunkLoadErrorHandler } from './core/services/chunk-load-error-handler.service';
import { PrimeOverlayLifecycleService } from './core/services/prime-overlay-lifecycle.service';
import { TimezoneInterceptor } from './core/interceptors/timezone.interceptor';
import { MAPBOX_ACCESS_TOKEN } from './core/tokens/mapbox.token';
import { configureMapboxGl } from './core/mapbox/configure-mapbox-gl';
import { PrimeNGTranslationService } from './shared/services/primeng-translation.service';
import { AssetTemplateMissingTranslationHandler } from './shared/i18n/asset-template-missing-translation.handler';
import { defaultTheme } from './core/theme.config';
import { STORAGE_KEYS } from './shared/constants/storage-keys';
import { environment } from '../environments/environment';

registerLocaleData(localeEn);
registerLocaleData(localeEs);

const TRANSLATE_INIT_TIMEOUT_MS = 10_000;

export function authBootstrapInit(authBootstrap: AuthBootstrapService): Promise<void> {
  return firstValueFrom(authBootstrap.ensureSessionReady()).then(() => undefined);
}

export function translateInit(translate: TranslateService): Promise<void> {
  const fallbackLang = environment.defaultLanguage;
  const supportedLangs = new Set(['en', 'es']);

  const sessionLang =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEYS.SESSION_LANGUAGE) : null;
  const preferredLang =
    typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.PREFERRED_LANGUAGE) : null;

  const storedLang = sessionLang || preferredLang;
  const initialLang = storedLang && supportedLangs.has(storedLang) ? storedLang : fallbackLang;

  return firstValueFrom(
    translate.use(initialLang).pipe(
      timeout(TRANSLATE_INIT_TIMEOUT_MS),
      catchError(() => translate.use(fallbackLang)),
    ),
  ).then(() => undefined);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => inject(RegistrationTrackingService).initialize()),
    { provide: MAPBOX_ACCESS_TOKEN, useValue: environment.mapboxAccessToken ?? '' },
    MessageService,
    ConfirmationService,
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    provideHttpClient(withInterceptors([TimezoneInterceptor, AuthInterceptor]), withJsonpSupport()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),

    providePrimeNG({
      ripple: true,
      theme: {
        preset: defaultTheme,
        options: {
          cssLayer: { name: 'primeng', order: 'theme, base, primeng, toast-overrides' },
          darkModeSelector: false,
        },
      },
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
        monthNames: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December',
        ],
        monthNamesShort: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
        today: 'Today',
        clear: 'Clear',
      },
    }),

    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: `.json?v=${environment.version}`,
      }),
      fallbackLang: environment.defaultLanguage,
      missingTranslationHandler: provideMissingTranslationHandler(
        AssetTemplateMissingTranslationHandler
      ),
    }),

    provideAppInitializer(() => {
      configureMapboxGl(inject(PLATFORM_ID));
    }),
    provideAppInitializer(() => authBootstrapInit(inject(AuthBootstrapService))),
    provideAppInitializer(() => translateInit(inject(TranslateService))),
    provideAppInitializer(() => {
      inject(PrimeNGTranslationService);
    }),
    provideAppInitializer(() => {
      inject(PrimeOverlayLifecycleService);
    }),
  ],
};
